import type { ReactNode } from 'react'
import type { GlassHandle } from '@/types'

import { forwardRef, useImperativeHandle, useRef } from 'react'

const ONLY_R = '1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0'
const ONLY_G = '0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0'
const ONLY_B = '0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0'

/**
 * §7.2 — the one shared refraction filter, referenced by all nine discs via
 * `backdrop-filter: url(#vis-glass)`. Three displacement passes (R over-refracts,
 * B under-refracts) are isolated to their channel and screen-blended back, so the
 * chromatic fringe only appears at the rim where light actually bends.
 */
export const GlassDefs = forwardRef<GlassHandle>((_props, ref): ReactNode => {
    const imageRef = useRef<SVGFEImageElement>(null)
    const rRef = useRef<SVGFEDisplacementMapElement>(null)
    const gRef = useRef<SVGFEDisplacementMapElement>(null)
    const bRef = useRef<SVGFEDisplacementMapElement>(null)

    useImperativeHandle(ref, () => ({
        setMap: (url: string, size: number): void => {
            const node = imageRef.current
            if (!node) return

            node.setAttribute('href', url)
            node.setAttribute('x', '0')
            node.setAttribute('y', '0')
            node.setAttribute('width', String(size))
            node.setAttribute('height', String(size))
        },
        setScales: (r: number, g: number, b: number): void => {
            rRef.current?.setAttribute('scale', r.toFixed(3))
            gRef.current?.setAttribute('scale', g.toFixed(3))
            bRef.current?.setAttribute('scale', b.toFixed(3))
        }
    }))

    return (
        <svg className='vis-defs' aria-hidden='true' focusable='false'>
            <defs>
                <filter
                    id='vis-glass'
                    x='0%'
                    y='0%'
                    width='100%'
                    height='100%'
                    colorInterpolationFilters='sRGB'
                >
                    <feImage
                        ref={imageRef}
                        result='vis-map'
                        preserveAspectRatio='none'
                    />

                    <feDisplacementMap
                        ref={rRef}
                        in='SourceGraphic'
                        in2='vis-map'
                        scale='0'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='vis-dr'
                    />
                    <feColorMatrix in='vis-dr' type='matrix' values={ONLY_R} result='vis-r' />

                    <feDisplacementMap
                        ref={gRef}
                        in='SourceGraphic'
                        in2='vis-map'
                        scale='0'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='vis-dg'
                    />
                    <feColorMatrix in='vis-dg' type='matrix' values={ONLY_G} result='vis-g' />

                    <feDisplacementMap
                        ref={bRef}
                        in='SourceGraphic'
                        in2='vis-map'
                        scale='0'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='vis-db'
                    />
                    <feColorMatrix in='vis-db' type='matrix' values={ONLY_B} result='vis-b' />

                    <feBlend in='vis-r' in2='vis-g' mode='screen' result='vis-rg' />
                    <feBlend in='vis-rg' in2='vis-b' mode='screen' />
                </filter>
            </defs>
        </svg>
    )
})

GlassDefs.displayName = 'GlassDefs'
